import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { ReconciliationService } from '../../Modules/Reconciliation/Services/reconciliation.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE, MAX_PAGE_SIZE } from '../../Consts/backoffice.const';
import {
  ALERT_OUTCOME_LABEL,
  RECONCILIATION_RAN_MESSAGE,
  ROLE_RECONCILIATION_RUN,
} from '../../Consts/reconciliation.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { OrganizationSummary } from '../../Models/backoffice/organization-summary.model';
import { ReconciliationFinding } from '../../Models/backoffice/reconciliation-finding.model';
import { ReconciliationReport } from '../../Models/backoffice/reconciliation-report.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationListResponse } from '../../Interfaces/api/backoffice/organization.interface';
import {
  ReconciliationFindingResponse,
  ReconciliationLatestResponse,
  ReconciliationReportsResponse,
  ReconciliationRunResponse,
} from '../../Interfaces/api/backoffice/reconciliation.interface';

@Injectable()
export class ReconciliationFacade {
  private readonly _reconciliationService = inject(ReconciliationService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _organizationId = signal<string>('');
  private readonly _reportPage = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isRunning = signal<boolean>(false);
  private readonly _lastRun = signal<ReconciliationRunResponse | null>(null);

  private readonly _organizations = httpResource<LaravelDefaultResponse<OrganizationListResponse>>(
    () => this._organizationService.listRequest({}, FIRST_PAGE, MAX_PAGE_SIZE),
  );

  private readonly _latest = httpResource<LaravelDefaultResponse<ReconciliationLatestResponse>>(() =>
    this._reconciliationService.latestRequest(),
  );

  private readonly _reports = httpResource<LaravelDefaultResponse<ReconciliationReportsResponse>>(
    () => this._reconciliationService.reportsRequest(this._reportPage(), this._pageSize()),
  );

  private readonly _finding = httpResource<LaravelDefaultResponse<ReconciliationFindingResponse>>(
    () =>
      this._organizationId()
        ? this._reconciliationService.inspectRequest(this._organizationId())
        : undefined,
  );

  readonly organizationId = computed<string>(() => this._organizationId());

  readonly organizations = computed<OrganizationSummary[]>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizations)).items,
  );

  readonly organizationName = computed<string>(
    () =>
      this.organizations().find((organization) => organization.id === this._organizationId())
        ?.displayName ?? '',
  );

  readonly latestReport = computed<ReconciliationReport | null>(() =>
    this._reconciliationService.toLatestReport(this._resourceState.valueOf(this._latest)),
  );

  readonly shapes = computed<string[]>(() =>
    this._reconciliationService.toShapes(this._resourceState.valueOf(this._latest)),
  );

  readonly latestState = computed<MetricState>(() =>
    this._resourceState.metricState(this._latest.status()),
  );

  readonly reports = computed<ReconciliationReport[]>(() =>
    this._reconciliationService.toReports(this._resourceState.valueOf(this._reports)),
  );

  readonly reportTotal = computed<number>(() =>
    this._reconciliationService.toReportTotal(this._resourceState.valueOf(this._reports)),
  );

  readonly reportsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._reports.status()),
  );

  readonly reportPage = computed<number>(() => this._reportPage());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly finding = computed<ReconciliationFinding | null>(() =>
    this._reconciliationService.toFinding(this._resourceState.valueOf(this._finding)),
  );

  readonly findingState = computed<MetricState>(() =>
    this._resourceState.metricState(this._finding.status()),
  );

  readonly isRunning = computed<boolean>(() => this._isRunning());

  readonly hasOrganization = computed<boolean>(() => this._organizationId() !== '');

  readonly canRun = computed<boolean>(
    () => this._operatorFacade.atLeast(ROLE_RECONCILIATION_RUN) && !this._isRunning(),
  );

  readonly lastRunFindings = computed<ReconciliationFinding[]>(() =>
    (this._lastRun()?.findings ?? []).map((row) => new ReconciliationFinding(row)),
  );

  readonly lastRunAlertOutcome = computed<string>(() => {
    const outcome = this._lastRun()?.alert_outcome ?? null;

    return outcome === null ? '' : (ALERT_OUTCOME_LABEL[outcome] ?? outcome);
  });

  selectOrganization(organizationId: string): void {
    this._organizationId.set(organizationId);
  }

  goToReportPage(page: number): void {
    this._reportPage.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._reportPage.set(FIRST_PAGE);
  }

  reload(): void {
    this._latest.reload();
    this._reports.reload();
    this._finding.reload();
  }

  runPortfolio(): void {
    if (!this.canRun()) {
      return;
    }

    this._isRunning.set(true);

    this._reconciliationService
      .runPortfolio()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) => this._afterRun(result),
        error: (error) => this._failRun(error),
      });
  }

  runForOrganization(): void {
    if (!this.canRun() || !this.hasOrganization()) {
      return;
    }

    this._isRunning.set(true);

    this._reconciliationService
      .runForOrganization(this._organizationId())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) => this._afterRun(result),
        error: (error) => this._failRun(error),
      });
  }

  private _afterRun(result: ReconciliationRunResponse): void {
    this._isRunning.set(false);
    this._lastRun.set(result);
    this._latest.reload();
    this._reports.reload();
    this._finding.reload();
    this._msgService.showSnackBar(RECONCILIATION_RAN_MESSAGE, { type: 'success' });
  }

  private _failRun(error: unknown): void {
    this._isRunning.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
